import { Type, Extension } from 'main.core';
import { lpad, getDateForLog, getTimeForLog, getLogMessage, logToString, isConsoleLogsEnabled, setConsoleLogsEnabled } from './log-helpers';
import { CallScheme, CallState, Provider, RoomType } from './engine/types';
import {
	ClientPlatform,
	ClientVersion,
	MediaStreamsKinds,
	JoinRequestFailedCodes,
} from './sdk/const';
import { JoinResponseError } from './sdk/errors';
import { CallTokenManager } from 'call.lib.call-token-manager';
import { CallSettingsManager } from 'call.lib.settings-manager';
import { stuckCallFinishTracker } from 'call.lib.stuck-call-finish-tracker';

import { Event } from 'main.core';

const MediaKind = {
	[MediaStreamsKinds.Camera]: 'video',
	[MediaStreamsKinds.Microphone]: 'audio',
	[MediaStreamsKinds.Screen]: 'sharing',
	[MediaStreamsKinds.ScreenAudio]: 'sharingAudio',
};
import { CallCommonRecordState } from './call_common_record';

const blankAvatar = '/bitrix/js/im/images/blank.gif';

let userData = {}
let usersInProcess = {}

let abortController = null;

/* User role & room permission */
let roomPermissions =
{
	AudioEnabled: false,
	VideoEnabled: false,
	ScreenShareEnabled: false,
};

let userPermissions =
{
	ask: false,
	audio: true,
	can_approve: false,
	change_role: false,
	change_settings: false,
	end_call: false,
	give_permissions: false,
	invite: false,
	join_call: false,
	kick_user: false,
	mute: false,
	mute_others: false,
	record_call: false,
	screen_share: true,
	update: false,
	video: true,
	view_users: false,
};

const UsersRoles = {
	ADMIN: 'ADMIN', // chat admin
	MANAGER: 'MANAGER', // aka moderator
	USER: 'USER', // regular user
};

const MediaPermissions = {
	MICROPHONE: 'mic',
	CAMERA: 'cam',
	SCREEN_SHARE: 'screenshare',
};

const regularUserRoles = [UsersRoles.USER]; // TODO got this from signaling in future

let currentUserRole = UsersRoles.USER;

function setCurrentUserRole(role)
{
	if (role)
	{
		currentUserRole = role.toUpperCase();
	}
}

function setRoomPermissions(_roomPermissions)
{
	if (!Type.isPlainObject(_roomPermissions))
	{
		return;
	}

	roomPermissions = _roomPermissions;
}

function setUserPermissionsByRoomPermissions(_roomPermissions)
{
	if (this.isRegularUser(this.getCurrentUserRole()))
	{
		let permissions = this.getUserPermissions();

		for (let permission in _roomPermissions)
		{
			if (_roomPermissions.hasOwnProperty(permission))
			{
				switch (permission)
				{
					case 'AudioEnabled':
						permissions.audio = _roomPermissions[permission];
						break;
					case 'VideoEnabled':
						permissions.video = _roomPermissions[permission];
						break;
					case 'ScreenShareEnabled':
						permissions.screen_share = _roomPermissions[permission];
						break;
				}
			}
		}

		this.setUserPermissions(permissions);
	}
}

function updateUserPermissionByNewRoomPermission(_roomPermission, value)
{
	if (this.isRegularUser(this.getCurrentUserRole()))
	{
		let permissions = this.getUserPermissions();

		switch (_roomPermission)
		{
			case 'audio':
				permissions.audio = value;
				break;
			case 'video':
				permissions.video = value;
				break;
			case 'screen_share':
				permissions.screen_share = value;
				break;
		}

		this.setUserPermissions(permissions);
	}
}

function isRegularUser(_role)
{
	return regularUserRoles.includes(_role);
}

function getRoomPermissions()
{
	return roomPermissions;
}

function setUserPermissions(_userPermissions)
{
	if (Type.isPlainObject(_userPermissions))
	{
		for (let permission in _userPermissions)
		{
			if (userPermissions.hasOwnProperty(permission))
			{
				userPermissions[permission] = _userPermissions[permission];
			}
		}
	}
}

function getUserPermissions()
{
	return userPermissions;
}

function getCurrentUserRole()
{
	return currentUserRole;
}

function havePermissionToBroadcast(type)
{
	let havePermission = false;

	switch (type)
	{
		case MediaPermissions.MICROPHONE:
			havePermission = userPermissions.audio;
			break;

		case MediaPermissions.CAMERA:
			havePermission = userPermissions.video;
			break;

		case MediaPermissions.SCREEN_SHARE:
			havePermission = userPermissions.screen_share;
			break;
	}

	return havePermission;
}

function isBroadcastDisabled()
{
	return !havePermissionToBroadcast(MediaPermissions.MICROPHONE)
		&& !havePermissionToBroadcast(MediaPermissions.CAMERA)
		&& !havePermissionToBroadcast(MediaPermissions.SCREEN_SHARE);
}

function canControlChangeSettings()
{
	return !!(userPermissions.change_settings && getCurrentBitrixCall());
}

function canControlGiveSpeakPermission()
{
	return !!userPermissions.give_permissions;
}

function getUserRoleByUserId(userId)
{
	if (userData.hasOwnProperty(userId))
	{
		return userData[userId].role;
	}
}

/* ------ */

function updateUserData(callId, users)
{
	let usersToUpdate = [];
	for (let i = 0; i < users.length; i++)
	{
		if (userData.hasOwnProperty(users[i]))
		{
			continue;
		}

		usersToUpdate.push(users[i]);
	}

	const result = new Promise((resolve, reject) => {
		if (usersToUpdate.length === 0 || !callId)
		{
			resolve();

			return;
		}

		BX.ajax.runAction('call.CallManager.getUsers', {
			data: {
				callId,
				userIds: usersToUpdate,
			},
		}).then((response) => {
			const data = Type.isPlainObject(response.data) ? response.data : {};

			users.forEach((userId) => {
				if (data[userId])
				{
					userData[userId] = data[userId];
				}
				delete usersInProcess[userId];
			});

			resolve();
		}).catch((error) => {
			reject(error.answer);
		});
	});

	for (const userId of usersToUpdate)
	{
		usersInProcess[userId] = result;
	}

	return result;
}

function setUserData(users)
{
	// Merge instead of replace so partial updates don't drop fields that were populated by a previous fetch
	for (let userId in users)
	{
		userData[userId] = { ...(userData[userId] ?? {}), ...users[userId] };
	}

	//setCurrentUserRole(userData[CallEngine.getCurrentUserId()].role);
}


function getUser(callId, userId)
{
	return new Promise((resolve, reject) =>
	{
		if (userData.hasOwnProperty(userId))
		{
			return resolve(userData[userId]);
		}
		else if (usersInProcess.hasOwnProperty(userId))
		{
			usersInProcess[userId].then(() =>
			{
				return resolve(userData[userId]);
			});
		}
		else
		{
			updateUserData(callId, [userId]).then(() =>
			{
				return resolve(userData[userId]);
			});
		}
	});
}

function getUserCached(userId)
{
	return userData.hasOwnProperty(userId) ? userData[userId] : null;
}

function getUsers(callId, users)
{

	return new Promise((resolve, reject) =>
	{
		updateUserData(callId, users).then(() =>
		{
			let result = {};
			users.forEach(userId => result[userId] = userData[userId] || {});
			return resolve(result);
		});
	});
}

function getUserName(callId, userId)
{
	return new Promise((resolve, reject) =>
	{
		if (userData.hasOwnProperty(userId))
		{
			return resolve(userData[userId].name ? userData[userId].name : '');
		}
		else if (usersInProcess.hasOwnProperty(userId))
		{
			usersInProcess[userId].then(() =>
			{
				return resolve(userData[userId].name ? userData[userId].name : '');
			});
		}
		else
		{
			updateUserData(callId, [userId]).then(() =>
			{
				return resolve(userData[userId].name ? userData[userId].name : '');
			});
		}
	});
}

function getUserAvatar(callId, userId)
{
	return new Promise((resolve, reject) =>
	{
		if (userData.hasOwnProperty(userId))
		{
			return resolve(userData[userId].avatar_hr && !isBlank(userData[userId].avatar_hr) ? userData[userId].avatar_hr : '');
		}
		else if (usersInProcess.hasOwnProperty(userId))
		{
			usersInProcess[userId].then(() =>
			{
				return resolve(userData[userId].avatar_hr && !isBlank(userData[userId].avatar_hr) ? userData[userId].avatar_hr : '');
			});
		}
		else
		{
			updateUserData(callId, [userId]).then(() =>
			{
				return resolve(userData[userId].avatar_hr && !isBlank(userData[userId].avatar_hr) ? userData[userId].avatar_hr : '');
			});
		}
	});
}

function getUserAvatars(callId, users)
{
	return new Promise((resolve, reject) =>
	{
		updateUserData(callId, users).then(() =>
		{
			let result = {};
			users.forEach((userId) =>
			{
				result[userId] = userData[userId].avatar_hr && !isBlank(userData[userId].avatar_hr) ? userData[userId].avatar_hr : ''
			});
			return resolve(result);
		});
	});
}

function isAvatarBlank(url)
{
	return isBlank(url);
}

function getCustomMessage(message, userData)
{
	let messageText;
	if (!Type.isPlainObject(userData))
	{
		userData = {};
	}

	if (userData.gender && BX.message.hasOwnProperty(message + '_' + userData.gender))
	{
		messageText = BX.message(message + '_' + userData.gender);
	}
	else
	{
		messageText = BX.message(message);
	}

	if (!messageText)
	{
		return '';
	}

	userData = convertKeysToUpper(userData);

	return messageText.replace(/#.+?#/gm, function (match)
	{
		const placeHolder = match.substr(1, match.length - 2);
		return userData.hasOwnProperty(placeHolder) ? userData[placeHolder] : match;
	});
}

function convertKeysToUpper(obj)
{
	var result = BX.util.objectClone(obj);

	for (let k in result)
	{
		const u = k.toUpperCase();

		if (u != k)
		{
			result[u] = result[k];
			delete result[k];
		}
	}
	return result;
}

function appendChildren(parent, children)
{
	children.forEach(child => parent.appendChild(child))
}

function containsVideoTrack(stream: MediaStream)
{
	if (!(stream instanceof MediaStream))
	{
		return false;
	}

	return stream.getVideoTracks().length > 0;
}

function hasHdVideo(stream: MediaStream)
{
	if (!(stream instanceof MediaStream) || stream.getVideoTracks().length === 0)
	{
		return false;
	}

	var videoTrack = stream.getVideoTracks()[0];
	var trackSettings = videoTrack.getSettings();

	return trackSettings.width >= 1280;
}

function findBestElementSize(width, height, userCount, minWidth, minHeight)
{
	minWidth = minWidth || 0;
	minHeight = minHeight || 0;
	let bestFilledArea = 0;

	for (let i = 1; i <= userCount; i++)
	{
		const area = getFilledArea(width, height, userCount, i);
		if (area.area > bestFilledArea && area.elementWidth > minWidth && area.elementHeight > minHeight)
		{
			bestFilledArea = area.area;
			var bestWidth = area.elementWidth;
			var bestHeight = area.elementHeight;
		}
		if (area.area < bestFilledArea)
		{
			break;
		}
	}
	if (bestFilledArea === 0)
	{
		bestWidth = minWidth;
		bestHeight = minHeight
	}
	return {width: bestWidth, height: bestHeight};
}

function getFilledArea(width, height, userCount, rowCount)
{
	const columnCount = Math.ceil(userCount / rowCount);
	const maxElementWidth = Math.floor(width / columnCount);
	const maxElementHeight = Math.floor(height / rowCount);

	const ratio = maxElementHeight / maxElementWidth;
	const neededRatio = 9 / 16;

	let expectedElementHeight;
	let expectedElementWidth;

	if (ratio < neededRatio)
	{
		expectedElementHeight = maxElementHeight;
		expectedElementWidth = Math.floor(maxElementWidth * (ratio / neededRatio));
	}
	else
	{
		expectedElementWidth = maxElementWidth;
		expectedElementHeight = Math.floor(maxElementHeight * (neededRatio / ratio));
	}

	//console.log(expectedElementWidth + 'x' + expectedElementHeight)
	var area = expectedElementWidth * expectedElementHeight * userCount;

	return {area: area, elementWidth: expectedElementWidth, elementHeight: expectedElementHeight};
}

const isWebRTCSupported = () =>
{
	return (typeof webkitRTCPeerConnection != 'undefined' || typeof mozRTCPeerConnection != 'undefined' || typeof RTCPeerConnection != 'undefined');
}

const isCallServerAllowed = () =>
{
	return BX.message('call_server_enabled') === 'Y'
}

const isFeedbackAllowed = () =>
{
	return BX.message('call_allow_feedback') === 'Y'
}

const shouldCollectStats = () =>
{
	return BX.message('call_collect_stats') === 'Y'
}

const shouldShowDocumentButton = () =>
{
	return BX.message('call_docs_status') !== 'N' || BX.message('call_resumes_status') !== 'N';
}

const getDocumentsArticleCode = () =>
{
	if (!BX.message('call_docs_status').startsWith('L'))
	{
		return false;
	}

	return BX.message('call_docs_status').substr(2);
}

const getResumesArticleCode = () =>
{
	if (!BX.message('call_resumes_status').startsWith('L'))
	{
		return false;
	}

	return BX.message('call_resumes_status').substr(2);
}

const getUserLimit = () =>
{
	if (isCallServerAllowed())
	{
		return parseInt(BX.message('call_server_max_users'));
	}

	return parseInt(BX.message('turn_server_max_users'));
}

const getClientSelfTestUrl = () =>
{
	return BX.message('call_client_selftest_url') ?? '';
}

const getCallFeatures = () => {
	return BX.message('call_features')
}


const getUuidv4 = () =>
{
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) =>
	{
		const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
		return v.toString(16);
	});
}

function reportConnectionResult(callId, connectionResult)
{
	BX.ajax.runAction("call.CallManager.reportConnection", {
		data: {
			callId: callId,
			connectionResult: connectionResult
		}
	})
}

function sendTelemetryEvent(options)
{

}

const isDesktop = () =>
{
	return typeof (BXDesktopSystem) != "undefined" || typeof (BXDesktopWindow) != "undefined";
}

const getBrowserForStatistics = () =>
{
	if (BX.browser.IsOpera())
	{
		return 'opera';
	}
	if (BX.browser.IsChrome())
	{
		return 'chrome';
	}
	if (BX.browser.IsFirefox())
	{
		return 'firefox';
	}
	if (BX.browser.IsSafari())
	{
		return 'safari';
	}
	return 'other';
}

function isBlank(url)
{
	return typeof (url) !== "string" || url == "" || url.endsWith(blankAvatar);
}

function stopMediaStreamAudioTracks(mediaStream)
{
	if (!mediaStream instanceof MediaStream)
	{
		return;
	}

	mediaStream.getAudioTracks().forEach(function (track)
	{
		if (track.kind === 'audio') {
			track.stop();
			mediaStream.removeTrack(track);
		}
	});
}

function stopMediaStreamVideoTracks(mediaStream)
{
	if (!mediaStream instanceof MediaStream)
	{
		return;
	}

	mediaStream.getTracks().forEach(function (track)
	{
		if (track.kind === 'video') {
			track.stop();
			mediaStream.removeTrack(track);
		}
	});
}

function stopMediaStream(mediaStream)
{
	if (!mediaStream instanceof MediaStream)
	{
		return;
	}

	mediaStream.getTracks().forEach(function (track)
	{
		track.stop()
	});
}

function getConferenceProvider(): string{
	if (isCallServerAllowed())
	{
		return Provider.Bitrix;
	}

	return Provider.Plain;
}

const getRoomType = (provider: string, chatId?: number): RoomType => {
	if (isLargeCallEnabled())
	{
		return RoomType.Large;
	}

	if (chatId)
	{
		const bigRoomChats = Extension.getSettings('call.core')?.call?.bigRoomChats || [];

		if (bigRoomChats.includes(chatId))
		{
			return RoomType.Large;
		}
	}

	return RoomType.Small;
};

const _engines = [];
let _primaryEngine = null;

function registerEngine(engine, isPrimary = false)
{
	_engines.push(engine);
	if (isPrimary)
	{
		_primaryEngine = engine;
	}
}

/**
 * Returns the first call; use for testing purposes only.
 * @returns The call that matches the provider, or null if no call is found.
 */
function getCurrentCall()
{
	for (const engine of _engines)
	{
		for (const callId in engine.calls)
		{
			if (engine.calls[callId].state === CallState.Connected)
			{
				return engine.calls[callId];
			}
		}
	}

	return null;
}

function getCurrentBitrixCall()
{
	for (const engine of _engines)
	{
		for (const callId in engine.calls)
		{
			if (engine.calls[callId].provider === Provider.Bitrix)
			{
				return engine.calls[callId];
			}
		}
	}

	return false;
}

function setCodecToReport(report, codecs, reportsWithoutCodecs)
{
	if (codecs[report.codecId])
	{
		report.codecName = codecs[report.codecId];
		return true;
	}
	return false;
}

function saveReportWithoutCodecs(report, reportsWithoutCodecs)
{
	if (reportsWithoutCodecs[report.codecId])
	{
		reportsWithoutCodecs[report.codecId].push(report);
	}
	else
	{
		reportsWithoutCodecs[report.codecId] = [report];
	}
}

function processReportsWithoutCodecs(report, codecs, reportsWithoutCodecs)
{
	codecs[report.id] = report.mimeType;
	if (reportsWithoutCodecs[report.id])
	{
		reportsWithoutCodecs[report.id].forEach(r =>
		{
			r.codecName = report.mimeType;
		});
		delete reportsWithoutCodecs[report.id];
	}
}

function setLocalPacketsLostOrSaveReport(report, remoteReports, reportsWithoutRemoteInfo)
{
	if (remoteReports[report.id])
	{
		const packetsLostData = calcLocalPacketsLost(report, remoteReports[report.id]);
		report.packetsLostData = packetsLostData;
		report.packetsLost = packetsLostData.totalPacketsLost;
		report.packetsLostExtended = formatPacketsLostData(packetsLostData);
		delete remoteReports[report.id];
		return true;
	}
	else if (!report.packetsLostExtended)
	{
		reportsWithoutRemoteInfo[report.id] = report;
		return false;
	}
}

function calcBitrate(currentReport, prevReport, isLocal)
{
	prevReport = prevReport || {};
	const bytes = isLocal
		? currentReport.bytesSent - (prevReport.bytesSent || 0)
		: currentReport.bytesReceived - (prevReport.bytesReceived || 0);
	const time = currentReport.timestamp - (prevReport.timestamp || 0);
	const bitrate = 8 * bytes / (time / 1000);
	return bitrate < 0 ? 0 : Math.trunc(bitrate);
}

function calcLocalPacketsLost(currentReport, prevReport, remoteReport)
{
	prevReport = prevReport || {};
	const packetsLost = Math.abs(remoteReport.packetsLost);
	const deltaPacketsSent = currentReport.packetsSent - (prevReport.packetsSent || 0);
	const deltaPacketsLost = packetsLost - (prevReport.packetsLost || 0);
	const percentPacketLost = (deltaPacketsLost / deltaPacketsSent) * 100 || 0;
	const percentPacketLostTotal = (packetsLost / currentReport.packetsSent) * 100 || 0;
	return {
		currentPacketsLost: deltaPacketsLost,
		currentPercentPacketLost: percentPacketLost >= 0 ? Math.trunc(percentPacketLost) : 0,
		totalPacketsLost: packetsLost,
		totalPercentPacketLost: Math.trunc(percentPacketLostTotal),
	};
}

function calcRemotePacketsLost(currentReport, prevReport)
{
	prevReport = prevReport || {};
	const packetsLost = Math.abs(currentReport.packetsLost);
	const deltaPacketsReceived = currentReport.packetsReceived - (prevReport.packetsReceived || 0);
	const deltaPacketsLost = packetsLost - (prevReport.packetsLost || 0);
	const percentPacketLost = (deltaPacketsLost / (deltaPacketsReceived + deltaPacketsLost)) * 100 || 0;
	const percentPacketLostTotal = (packetsLost / (currentReport.packetsReceived + packetsLost)) * 100 || 0;
	return {
		currentPacketsLost: deltaPacketsLost,
		currentPercentPacketLost:  percentPacketLost >= 0 ? Math.trunc(percentPacketLost) : 0,
		totalPacketsLost: packetsLost,
		totalPercentPacketLost:  Math.trunc(percentPacketLostTotal),
	};
}

function formatPacketsLostData(data)
{
	return `${data.currentPacketsLost} - ${data.currentPercentPacketLost}% (total: ${data.totalPacketsLost} - ${data.totalPercentPacketLost}%)`;
}

function calcConnectionScore(dataLoss)
{
	if (dataLoss <= 10)
	{
		return 4;
	}

	if (dataLoss > 10 && dataLoss <= 20)
	{
		return 3;
	}

	if (dataLoss > 20 && dataLoss <= 30)
	{
		return 2;
	}

	if (dataLoss > 30)
	{
		return 1;
	}
}

function getAvatarBackground()
{
	const colorList = ['#0154C8', '#1BCE7B', '#B15EF5', '#55D0E0', '#F85E9E', '#0075FF', '#00A94E'];

	return colorList[Math.floor(Math.random() * colorList.length)];
}

function getRecordTimeText(recordState, returnInSeconds = false)
{
	if (!recordState)
	{
		return '';
	}

	const nowDate = new Date();
	let startDate = new Date(recordState.date.start);

	if (startDate > nowDate)
	{
		startDate = nowDate;
	}

	const pauseTime = recordState.date.pause
		.map(({ start, finish }) => {
			const startTime = new Date(start);
			const finishTime = finish ? new Date(finish) : nowDate;

			return finishTime - startTime;
		})
		.reduce((sum, diff) => sum + diff, 0);

	let totalTime = nowDate - startDate - pauseTime;
	if (totalTime < 0)
	{
		totalTime = 0;
	}

	let seconds = Math.round(totalTime / 1000);
	if (returnInSeconds)
	{
		return seconds;
	}

	const hours = Math.floor(seconds / 3600);
	seconds %= 3600;

	const minutes = Math.floor(seconds / 60);
	seconds %= 60;

	return hours > 0
		? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
		: `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function getTimeText(startTime)
{
	if (!startTime)
	{
		return '';
	}

	const nowDate = new Date();
	let startDate = new Date(startTime);
	if (startDate.getTime() < nowDate.getTime())
	{
		startDate = nowDate;
	}

	let totalTime = nowDate - startDate;
	if (totalTime <= 0)
	{
		totalTime = 0;
	}

	let second = Math.floor(totalTime / 1000);

	let hour = Math.floor(second / 60 / 60);
	if (hour > 0)
	{
		second -= hour * 60 * 60;
	}

	const minute = Math.floor(second / 60);
	if (minute > 0)
	{
		second -= minute * 60;
	}

	return (hour > 0 ? hour + ':' : '')
		+ (hour > 0 ? minute.toString().padStart(2, "0") + ':' : minute + ':')
		+ second.toString().padStart(2, "0")
		;
}

function getTimeInSeconds(startTime)
{
	if (!startTime)
	{
		return '';
	}

	const nowDate = new Date();
	let startDate = new Date(startTime);
	if (startDate.getTime() < nowDate.getDate())
	{
		startDate = nowDate;
	}

	let totalTime = nowDate - startDate;
	if (totalTime <= 0)
	{
		totalTime = 0;
	}

	return Math.floor(totalTime / 1000);
}

const isConferenceChatEnabled = () =>
{
	return BX.message('conference_chat_enabled');
}

/**
 * @param {string|Error} error
 * @returns {string}
 */
function getCallConnectionErrorCode(error)
{
	let errorCode = 'UNKNOWN_ERROR';
	if (Type.isString(error) && error)
	{
		errorCode = error;
	}
	else if (Type.isObject(error) && error instanceof JoinResponseError)
	{
		errorCode = error.name;
	}
	else if (Type.isObject(error) && error.code)
	{
		errorCode = error.code === 'access_denied' ? 'ACCESS_DENIED' : error.code;
	}

	return errorCode;
}

/**
 * @param {{message: string}|Error} error
 * @returns {string}
 */
function getCallConnectionErrorMessage(error)
{
	let errorMessage = error?.message ?? '';
	if (Type.isObject(error) && error instanceof Error && error.name !== 'Error' && !(error instanceof JoinResponseError))
	{
		// additional error type for js error
		errorMessage = `${error.name}: ${error.message}`;
	}

	return errorMessage;
}

const getCallConnectionData = async (callOptions, chatId, mustCreate = true) => {
	if (!Type.isPlainObject(callOptions))
	{
		return Promise.reject(new Error('Incorrect type of callOptions'));
	}

	return new Promise(async (resolve, reject) =>
	{
		try
		{
			abortController = new AbortController();
			const callBalancerUrl = CallSettingsManager.callBalancerUrl;
			const url = `${callBalancerUrl}/v2/join?mustCreate=${Boolean(mustCreate)}`;

			const userToken = await CallTokenManager.getUserToken(chatId);
			const isPlainCall = callOptions.provider === Provider.Plain;

			const data = JSON.stringify({
				userToken,
				isOneToOne: isPlainCall,
				clientVersion: ClientVersion,
				clientPlatform: ClientPlatform,
				...callOptions,
			});

			let responseInfo = { chatId };

			const getResponseInfoString = (ignoreKeys = []) => Object.entries(responseInfo)
				.filter(([key]) => !ignoreKeys.includes(key))
				.map(([key, value]) => `${key}: ${value}`).join(', ');

			fetch(url, {
				method: 'POST',
				body: data,
				signal: abortController.signal,
			})
				.catch((error) => {
					if (error instanceof TypeError)
					{
						throw new JoinResponseError(error, JoinRequestFailedCodes.FailedRequest);
					}
					else
					{
						throw error;
					}
				})
				.then(async (response) => {
					responseInfo = { ...responseInfo, status: response?.status, ok: response?.ok };

					// Non-OK responses may carry non-JSON bodies
					if (!response.ok)
					{
						let text;
						try
						{
							text = await response.text();
						}
						catch (error)
						{
							if (error?.name === 'AbortError')
							{
								throw error;
							}
							throw new JoinResponseError(error, JoinRequestFailedCodes.BodyReadError);
						}
						try
						{
							return text
								? JSON.parse(text)
								: '';
						}
						catch
						{
							return text.replaceAll(/<[^>]*>/g, ' ').replaceAll(/\s+/g, ' ').trim();
						}
					}

					try
					{
						return await response.json();
					}
					catch (error)
					{
						if (error instanceof SyntaxError || error?.name === 'AbortError')
						{
							throw error;
						}
						throw new JoinResponseError(error, JoinRequestFailedCodes.BodyReadError);
					}
				})
				.catch((error) => {
					if (Type.isObject(error) && error.name === 'AbortError')
					{
						throw new JoinResponseError(error, JoinRequestFailedCodes.AbortedRequest);
					}
					else if (error instanceof SyntaxError)
					{
						throw new JoinResponseError(error, JoinRequestFailedCodes.JsonParsingError);
					}
					else
					{
						throw error;
					}
				})
				.then((response) => {
					if (!responseInfo?.ok)
					{
						if (response?.error)
						{
							// Stuck call on join (mustCreate=false): the media room is gone,
							// finish the DB record silently so it disappears from recent.
							// On mustCreate=true the same codes mean "can not create" — skip.
							// Debounce via stuckCallFinishTracker: delay the REST by
							// DEBOUNCE_MS so a concurrent Pull `Call::finish` (the usual
							// path when the backend already closed the call) can cancel
							// it before it hits the server. Engine pull handlers call
							// stuckCallFinishTracker.cancelPending(id, uuid) on arrival.
							const errorCode = response.error?.code;
							if (
								mustCreate === false
								&& callOptions.callUuid
								&& (
									errorCode === JoinRequestFailedCodes.RoomNotFound
									|| errorCode === JoinRequestFailedCodes.CanNotCreateRoom
								)
							)
							{
								stuckCallFinishTracker.scheduleFinish(null, callOptions.callUuid, () => BX.ajax.runAction('call.CallManager.finish', {
									data: {
										callUuid: callOptions.callUuid,
										silent: true,
									},
								}));
							}

							throw new JoinResponseError(response?.error?.message, response?.error?.code);
						}
						else
						{
							const payload = Type.isString(response) ? response : JSON.stringify(response);
							throw new JoinResponseError(payload, JoinRequestFailedCodes.UnexpectedResponse);
						}
					}
					resolve(response);
				})
				.catch((rawError) => {
					const error = rawError;
					if (error?.message)
					{
						error.message = `${getResponseInfoString([
							'ok',
							...error?.code === JoinRequestFailedCodes.JsonParsingError
								? ['status']
								: [],
						])}, ${error.message}`;
					}

					try
					{
						if (error?.xhr?.responseText)
						{
							const response = JSON.parse(error.xhr.responseText);
							if (response.error)
							{
								reject(response);
							}
						}
					}
					catch (e)
					{
						reject(e);
					}

					reject(error);
				})
				.finally(() => {
					abortController = null;
				});
		}
		catch (error)
		{
			reject(error);
		}
	});
};

const getCallConnectionDataById = async (callUuid) => {
	try
	{
		const call = await _primaryEngine.getCallWithId(callUuid);
		const chatId = call.call.associatedEntity.chatId;
		const roomType = getRoomType(call.call.provider, chatId);

		return getCallConnectionData({
			roomType,
			callType: call.call.type,
			instanceId: call.call.instanceId,
			provider: call.call.provider,
			callToken: CallTokenManager.getTokenCached(chatId),
			callUuid,
		}, call.call.associatedEntity.chatId, false);
	}
	catch (error)
	{
		throw error;
	}
};

const abortGetCallConnectionData = () => {
	abortController?.abort();
};

function startSelfTest()
{
	const link = BX.Call.Util.getClientSelfTestUrl();

	if (isDesktop()) {
		window.open(link, '_blank', 'popup');
		return;
	}

	window.open(link, '_blank');
}

const useTcpSdp = () =>
{
	return BX.message('call_use_tcp_sdp') === 'Y';
}

function openArticle(articleCode)
{
	const infoHelper = BX.UI.InfoHelper;

	if (infoHelper.isOpen())
	{
		infoHelper.close()
	}

	infoHelper.show(articleCode);
}

const getAiSettings = () =>
{
	return Extension.getSettings('call.core')?.ai || {};
}

const isLargeCallEnabled = () => {
	return BX.message('force_large_calls') === 'Y';
};

const canUseNewCallApi = (roomType) => {
	return roomType === RoomType.Large
		|| (isLargeCallEnabled() && roomType !== RoomType.Small);
};

const getSyncCallInvitePeriod = () =>
{
	return Extension.getSettings('call.core')?.call?.syncCallInvitePeriod;
}

const getCloudRecordSettings = () => {
	return Extension.getSettings('call.core')?.cloudRecord || {}
}

const isUserControlFeatureEnabled = () =>
{
	return Extension.getSettings('call.core')?.isUserControlFeatureEnabled;
}

const isDisableCameraNewJoinedUsersFeatureEnabled = () =>
{
	return Extension.getSettings('call.core')?.isDisableCameraNewJoinedUsersFeatureEnabled;
}

const countDisableCameraNewJoinedUsersFeature = () =>
{
	return Extension.getSettings('call.core')?.countDisableCameraNewJoinedUsersFeature;
}

const isStreamQualityFeatureEnabled = () =>
{
	return Extension.getSettings('call.core')?.isStreamQualityFeatureEnabled;
}

const isPictureInPictureFeatureEnabled = () =>
{
	return Extension.getSettings('call.core')?.isPictureInPictureFeatureEnabled;
}

const isMetricsEnabled = () =>
{
	return Extension.getSettings('call.core')?.isMetricsEnabled;
}

const isMetricsLogsEnabled = () =>
{
	return Extension.getSettings('call.core')?.isMetricsLogsEnabled;
}

const isKibanaLogsEnabled = () =>
{
	return Extension.getSettings('call.core')?.isKibanaLogsEnabled;
}


const isChatMountInPage = () =>
{
	return Extension.getSettings('call.core')?.isAirDesignEnabled && Extension.getSettings('call.core')?.shouldHideQuickAccess;
}

const isCloudRecordEnabled = () => {
	return Extension.getSettings('call.core')?.isCloudRecordEnabled;
};

const isCloudRecordTariffEnabled = () => {
	return Extension.getSettings('call.core')?.isCloudRecordTariffEnabled;
};

const isCloudRecordLogEnabled = () => {
	return Extension.getSettings('call.core')?.isCloudRecordLogEnabled;
};

const isCommonRecordStateInactive = (state) => {
	return [CallCommonRecordState.Stopped, CallCommonRecordState.Destroyed].includes(state);
};

/* ws logger */

function sendLog(params)
{
	const evnt = new Event.BaseEvent({data: params});
	Event.EventEmitter.emit('BX.Call.Logger:sendLog', evnt);
}

function deepParseJSON(value)
{
	if (typeof value === 'string')
	{
		try
		{
			const parsed = JSON.parse(value);
			if (typeof parsed === 'object' && parsed !== null)
			{
				return deepParseJSON(parsed);
			}
			return parsed;
		}
		catch (e)
		{
			return value;
		}
	}
	else if (Array.isArray(value))
	{
		return value.map(item => deepParseJSON(item));
	}
	else if (typeof value === 'object' && value !== null)
	{
		const result = {};
		Object.keys(value).forEach(key => {
			result[key] = deepParseJSON(value[key]);
		});
		return result;
	}
	else
	{
		return value;
	}
}


function isLegacyCall(provider, scheme = null)
{
	if (scheme)
	{
		return scheme === CallScheme.classic;
	}

	const isLegacyPlainCall = provider === 'Plain' && !CallSettingsManager.isJwtInPlainCallsEnabled();
	const isLegacyBitrixCall = provider === 'Bitrix' && !CallSettingsManager.jwtCallsEnabled;

	return isLegacyPlainCall || isLegacyBitrixCall;
}

function getCallIdentifier(call)
{
	if (!call)
	{
		return null;
	}

	return isLegacyCall(call.provider, call.scheme) ? call.id : call.uuid;
}

function getClickLinkInterceptor()
{
	const handleClick = (event) => {
		const link = event.target.closest('a');

		if (!link)
		{
			return;
		}
		const href = link.getAttribute('href');

		if (!href)
		{
			return;
		}

		if (event.defaultPrevented)
		{
			return;
		}

		window.open(href, '_blank');
		event.preventDefault();
	};

	const startIntercepting = () => {
		document.addEventListener('click', handleClick, { capture: true });
	};

	const stopIntercepting = () => {
		document.removeEventListener('click', handleClick, { capture: true });
	};

	return {
		startIntercepting,
		stopIntercepting,
	};
}

function getDisconnectedUsers(currentCall)
{
	const result = [];
	if (!currentCall)
	{
		return result;
	}

	const userStates = currentCall.getUsers();

	for (let userId in userStates)
	{
		if (userStates[userId] !== BX.Call.UserState.Connected)
		{
			const userData = getUserCached(userId)
			if (userData)
			{
				result.push(userData);
			}
		}
	}

	return result;
}

const isVueEnabled = () => {
	return Extension.getSettings('call.core')?.isVueEnabled || false;
};

const getCallInvitePeriod = () => {
	return Extension.getSettings('call.core')?.call?.callInvitePeriod ?? 30000;
};

export default {
	MediaKind,
	updateUserData,
	setUserData,
	getDateForLog,
	getTimeForLog,
	lpad,
	getUser,
	getUserCached,
	getUsers,
	getUserName,
	getUserAvatar,
	getUserAvatars,
	isAvatarBlank,
	getCustomMessage,
	convertKeysToUpper,
	appendChildren,
	containsVideoTrack,
	hasHdVideo,
	findBestElementSize,
	getFilledArea,
	isWebRTCSupported,
	isCallServerAllowed,
	isFeedbackAllowed,
	shouldCollectStats,
	shouldShowDocumentButton,
	getDocumentsArticleCode,
	getResumesArticleCode,
	getUserLimit,
	getClientSelfTestUrl,
	getLogMessage,
	logToString,
	getUuidv4,
	reportConnectionResult,
	sendTelemetryEvent,
	isDesktop,
	getBrowserForStatistics,
	isBlank,
	stopMediaStream,
	stopMediaStreamVideoTracks,
	stopMediaStreamAudioTracks,
	getConferenceProvider,
	getRoomType,
	getCurrentCall,
	getCurrentBitrixCall,
	registerEngine,
	setCodecToReport,
	saveReportWithoutCodecs,
	processReportsWithoutCodecs,
	setLocalPacketsLostOrSaveReport,
	calcBitrate,
	calcLocalPacketsLost,
	calcRemotePacketsLost,
	formatPacketsLostData,
	calcConnectionScore,
	getCallFeatures,
	getAvatarBackground,
	getRecordTimeText,
	getTimeText,
	getTimeInSeconds,
	isConferenceChatEnabled,
	getCallConnectionData,
	getCallConnectionDataById,
	abortGetCallConnectionData,
	startSelfTest,
	useTcpSdp,
	openArticle,
	getAiSettings,
	isLargeCallEnabled,
	canUseNewCallApi,
	getSyncCallInvitePeriod,
	isStreamQualityFeatureEnabled,
	getCloudRecordSettings,
	isUserControlFeatureEnabled,
	isPictureInPictureFeatureEnabled,
	isMetricsEnabled,
	isMetricsLogsEnabled,
	sendLog,
	isChatMountInPage,
	roomPermissions,
	getCurrentUserRole,
	havePermissionToBroadcast,
	isBroadcastDisabled,
	setRoomPermissions,
	getRoomPermissions,
	setUserPermissions,
	getUserPermissions,
	updateUserPermissionByNewRoomPermission,
	setUserPermissionsByRoomPermissions,
	canControlChangeSettings,
	canControlGiveSpeakPermission,
	setCurrentUserRole,
	UsersRoles,
	isRegularUser,
	getUserRoleByUserId,
	isDisableCameraNewJoinedUsersFeatureEnabled,
	countDisableCameraNewJoinedUsersFeature,
	isKibanaLogsEnabled,
	isConsoleLogsEnabled,
	setConsoleLogsEnabled,
	deepParseJSON,
	isCloudRecordEnabled,
	isCloudRecordTariffEnabled,
	isCommonRecordStateInactive,
	isCloudRecordLogEnabled,
	getCallConnectionErrorCode,
	getCallConnectionErrorMessage,
	isLegacyCall,
	getCallIdentifier,
	getClickLinkInterceptor,
	getDisconnectedUsers,
	isVueEnabled,
	getCallInvitePeriod,
};
